import React, { useState } from "react";
import "./Admin.css";
import { addCompany } from "../../api/add-company";

const Admin = () => {
  interface FormData {
    companyName: string;
    isActive: boolean;
    duration: string;
    phoneNumber: string;
    email: string;
    address: string;
  }

  const [formData, setFormData] = useState<FormData>({
    companyName: "",
    isActive: false,
    duration: "",
    phoneNumber: "",
    email: "",
    address: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.companyName ||
      !formData.duration ||
      !formData.phoneNumber ||
      !formData.email ||
      !formData.address
    ) {
      setError("All fields are required.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!/^[\d+\-\s]{7,}$/.test(formData.phoneNumber)) {
      setError("Please enter a valid phone number.");
      return;
    }

    try {
      const response = await addCompany(formData);

      if (response.success) {
        setSuccess("Company added successfully!");
        setFormData({
          companyName: "",
          isActive: false,
          duration: "",
          phoneNumber: "",
          email: "",
          address: "",
        });
      } else {
        setError(
          response.message || "Failed to add company. Please try again."
        );
      }
    } catch (error) {
      console.error("Error adding company:", error);
      setError("An error occurred while adding the company. Please try again.");
    }
  };

  return (
    <section className="admin-container" aria-labelledby="add-company-title">
      <h1 id="add-company-title">Add Company</h1>
      <form
        onSubmit={handleSubmit}
        className="admin-form"
        autoComplete="off"
        aria-describedby="add-company-desc">
        <p id="add-company-desc" style={{ fontSize: "0.95rem", color: "#555" }}>
          Fill in the company details below. All fields are required.
        </p>
        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}
        {success && (
          <div className="success-message" role="status" aria-live="polite">
            {success}
          </div>
        )}
        <div className="form-group">
          <label htmlFor="companyName">Company Name</label>
          <input
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Enter company name"
            required
            aria-required="true"
          />
        </div>
        <div className="form-group">
          <label htmlFor="isActive">Activate</label>
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            aria-checked={formData.isActive}
          />
        </div>
        <div className="form-group">
          <label htmlFor="duration">Duration</label>
          <input
            type="text"
            id="duration"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            placeholder="Enter duration (e.g., 1 year)"
            required
            aria-required="true"
          />
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="Enter phone number"
            required
            aria-required="true"
            pattern="[\d+\-\s]{7,}"
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
            required
            aria-required="true"
          />
        </div>
        <div className="form-group">
          <label htmlFor="address">Address</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Enter address"
            rows={3}
            required
            aria-required="true"></textarea>
        </div>
        <button type="submit" className="submit-button">
          Submit
        </button>
      </form>
      <a href="/company-list" className="company-list-link">
        View Company List
      </a>
    </section>
  );
};

export default Admin;
